import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { FileDown, Printer, X, Loader2, FileText, Languages, Sparkles } from "lucide-react";
import api from "../../lib/api";

const LANGS = [
  { code: "de", label: "DE", full: "Deutsch" },
  { code: "fr", label: "FR", full: "Français" },
  { code: "lu", label: "LU", full: "Lëtzebuergesch" },
  { code: "en", label: "EN", full: "English" },
];

/**
 * Per-plant "PDF-Steckbrief" launcher. Fetches the PDF as a blob (so the
 * admin JWT / X-Admin-Pin header actually reaches the backend), then shows
 * an in-place preview with download + print buttons.
 */
export default function PlantPdfButton({ plant, variant = "icon" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [generatingPhotos, setGeneratingPhotos] = useState(false);
  const [jobProgress, setJobProgress] = useState(0);
  const [jobMessage, setJobMessage] = useState("");
  const [blobUrl, setBlobUrl] = useState(null);
  const [lang, setLang] = useState("de");
  const [availableLangs, setAvailableLangs] = useState({ de: true });
  const iframeRef = useRef(null);

  const loadAvailableLangs = async () => {
    try {
      const r = await api.get(`/admin/plants/${plant.id}/profile-data`);
      const trans = r.data.translations || {};
      setAvailableLangs({ de: true, ...Object.fromEntries(Object.keys(trans).map(l => [l, true])) });
    } catch { /* ignore */ }
  };

  const load = async (targetLang = lang) => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/plants/${plant.id}/steckbrief.pdf`, {
        responseType: "blob",
        params: { lang: targetLang },
      });
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      const url = URL.createObjectURL(r.data);
      setBlobUrl(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "PDF-Erzeugung fehlgeschlagen");
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadAvailableLangs();
      load("de");
      setLang("de");
    }
  }, [open]);

  const switchLang = async (newLang) => {
    if (newLang === lang || busy) return;
    setLang(newLang);
    if (newLang !== "de" && !availableLangs[newLang]) {
      // No translation yet – trigger it
      if (!window.confirm(`Es gibt noch keine ${LANGS.find(l => l.code === newLang)?.full}-Übersetzung. Jetzt mit Claude erzeugen? (dauert ca. 10–20 s)`)) {
        setLang(lang);
        return;
      }
      setTranslating(true);
      try {
        await api.post(`/admin/plants/${plant.id}/translate?lang=${newLang}`);
        toast.success(`Übersetzung ${LANGS.find(l => l.code === newLang)?.full} bereit`);
        setAvailableLangs((prev) => ({ ...prev, [newLang]: true }));
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Übersetzung fehlgeschlagen");
        setTranslating(false);
        setLang(lang);
        return;
      } finally {
        setTranslating(false);
      }
    }
    await load(newLang);
  };

  useEffect(() => {
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [blobUrl]);

  const close = () => {
    setOpen(false);
    if (blobUrl) { URL.revokeObjectURL(blobUrl); setBlobUrl(null); }
  };

  const download = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    const suffix = lang !== "de" ? `-${lang}` : "";
    a.download = `flyer-${plant.slug || plant.id}${suffix}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const print = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch {
      toast.error("Drucken fehlgeschlagen – bitte PDF herunterladen und aus dem Reader drucken");
    }
  };

  const generatePhotos = async () => {
    if (generatingPhotos || busy) return;
    const ok = window.confirm(
      "KI-Komplett-Füllen für diese Pflanze:\n\n" +
      "• Fehlende Texte (Beschreibung, Verwendung, Standort, Warnungen, Historie) via Claude\n" +
      "• Detaildaten (~40 Felder: Bestimmung, Heilwissen, Ökologie) via Claude\n" +
      "• Bis zu 5 Detail-Fotos + Titelbild + alle Rezept-Fotos via Nano Banana\n\n" +
      "Das läuft im Hintergrund (60-120 Sekunden). Du siehst live den Fortschritt.\n\nFortfahren?",
    );
    if (!ok) return;
    setGeneratingPhotos(true);
    setJobProgress(0);
    setJobMessage("Starte Job …");
    try {
      const r = await api.post(`/admin/plants/${plant.id}/ki-komplett-async`);
      const jobId = r.data?.job_id;
      if (!jobId) throw new Error("Kein job_id erhalten");
      // Poll every 3s until done/error
      const startedAt = Date.now();
      const maxMs = 5 * 60 * 1000; // 5 min hard stop
      while (Date.now() - startedAt < maxMs) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        const s = await api.get(`/admin/jobs/${jobId}`);
        const st = s.data || {};
        setJobProgress(st.progress || 0);
        setJobMessage(st.message || st.stage || "läuft …");
        if (st.status === "done") {
          const parts = [];
          const res = st.result || {};
          if (res.text_fields) parts.push(`${res.text_fields} Text-Feld${res.text_fields === 1 ? "" : "er"}`);
          if (res.profile_generated) parts.push("Detaildaten");
          if (res.hero) parts.push("Titelbild");
          if (res.detail_photos) parts.push(`${res.detail_photos} Detail-Foto${res.detail_photos === 1 ? "" : "s"}`);
          if (res.recipe_photos) parts.push(`${res.recipe_photos} Rezept-Foto${res.recipe_photos === 1 ? "" : "s"}`);
          if (parts.length) toast.success("Erzeugt: " + parts.join(", "));
          else toast.info("Alles bereits gefüllt – nichts zu erzeugen.");
          await load(lang);
          return;
        }
        if (st.status === "error") {
          toast.error("KI-Job fehlgeschlagen: " + (st.error || "unbekannt"));
          return;
        }
      }
      toast.error("Job-Timeout nach 5 Minuten – bitte Seite neu laden und erneut versuchen.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "KI-Füller fehlgeschlagen");
    } finally {
      setGeneratingPhotos(false);
      setJobProgress(0);
      setJobMessage("");
    }
  };

  const trigger = variant === "text" ? (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium border"
      style={{ borderColor: "var(--wp-border)", backgroundColor: "var(--wp-cream)", color: "var(--wp-forest)" }}
      data-testid={`pdf-steckbrief-btn-${plant.slug || plant.id}`}
      title="PDF-Steckbrief"
    >
      <FileText className="w-3.5 h-3.5" /> PDF
    </button>
  ) : (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className="px-2 inline-flex"
      style={{ color: "var(--wp-olive)" }}
      title="PDF-Steckbrief erstellen"
      data-testid={`pdf-steckbrief-btn-${plant.slug || plant.id}`}
    >
      <FileText className="w-4 h-4" />
    </button>
  );

  return (
    <>
      {trigger}
      {open && createPortal(
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-2 sm:p-4"
          style={{ backgroundColor: "rgba(26,47,28,0.7)" }}
          onClick={close}
          data-testid="pdf-steckbrief-modal"
        >
          <div
            className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 p-4 border-b" style={{ borderColor: "var(--wp-border)" }}>
              <div className="min-w-0">
                <div className="overline">PDF-Steckbrief</div>
                <div className="font-display text-lg truncate" style={{ color: "var(--wp-olive)" }}>{plant.name_de}</div>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <div
                  className="flex items-center gap-0.5 p-0.5 rounded-full"
                  style={{ backgroundColor: "var(--wp-cream)" }}
                  data-testid="pdf-steckbrief-lang-switch"
                  title="Sprache wählen"
                >
                  <Languages className="w-3.5 h-3.5 mx-1 opacity-60" />
                  {LANGS.map((l) => {
                    const active = l.code === lang;
                    const hasTranslation = availableLangs[l.code];
                    return (
                      <button
                        key={l.code}
                        onClick={() => switchLang(l.code)}
                        disabled={busy || translating}
                        className="px-2 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider"
                        style={{
                          backgroundColor: active ? "var(--wp-olive)" : "transparent",
                          color: active ? "var(--wp-cream)" : (hasTranslation ? "var(--wp-forest)" : "#B8B39A"),
                        }}
                        data-testid={`pdf-lang-${l.code}`}
                        title={hasTranslation ? l.full : `${l.full} – Übersetzung wird bei Klick erzeugt`}
                      >
                        {l.label}
                        {!hasTranslation && l.code !== "de" && <span className="ml-1">*</span>}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={generatePhotos}
                  disabled={busy || translating || generatingPhotos}
                  className="btn-secondary text-sm !py-2 !px-3"
                  style={{ borderColor: "var(--wp-olive)", color: "var(--wp-olive)" }}
                  data-testid="pdf-generate-photos"
                  title="Fehlende Texte, Detaildaten UND Fotos per KI erzeugen (~60 s)"
                >
                  {generatingPhotos ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  KI Komplett
                </button>
                <button
                  onClick={download}
                  disabled={!blobUrl}
                  className="btn-secondary text-sm !py-2 !px-3"
                  data-testid="pdf-steckbrief-download"
                >
                  <FileDown className="w-4 h-4" /> Download
                </button>
                <button
                  onClick={print}
                  disabled={!blobUrl}
                  className="btn-primary text-sm !py-2 !px-3"
                  data-testid="pdf-steckbrief-print"
                >
                  <Printer className="w-4 h-4" /> Drucken
                </button>
                <button
                  onClick={close}
                  className="ml-1 w-9 h-9 rounded-full inline-flex items-center justify-center"
                  style={{ backgroundColor: "var(--wp-cream)" }}
                  data-testid="pdf-steckbrief-close"
                  title="Schließen"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0 bg-[var(--wp-cream)]">
              {(busy || translating || generatingPhotos) && !blobUrl && (
                <div className="h-full flex flex-col items-center justify-center text-sm opacity-80 gap-3 px-6">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <div className="text-center">
                    {generatingPhotos ? (jobMessage || "KI läuft im Hintergrund …")
                      : translating ? "Claude übersetzt gerade …"
                      : "Steckbrief wird erstellt …"}
                  </div>
                  {generatingPhotos && jobProgress > 0 && (
                    <div className="w-64 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-[var(--wp-primary)] transition-all" style={{ width: `${jobProgress}%` }} />
                    </div>
                  )}
                </div>
              )}
              {generatingPhotos && blobUrl && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center pointer-events-none">
                  <div className="px-5 py-3 rounded-2xl bg-white text-sm shadow-lg flex flex-col items-center gap-2 min-w-[280px]">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>{jobMessage || "KI läuft im Hintergrund …"}</span>
                    </div>
                    <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-[var(--wp-primary)] transition-all" style={{ width: `${jobProgress}%` }} />
                    </div>
                    <div className="text-xs opacity-60">{jobProgress}%</div>
                  </div>
                </div>
              )}
              {blobUrl && (
                <iframe
                  ref={iframeRef}
                  src={blobUrl}
                  title={`Steckbrief ${plant.name_de}`}
                  className="w-full h-full min-h-[70vh]"
                  style={{ border: 0 }}
                  data-testid="pdf-steckbrief-iframe"
                />
              )}
            </div>
            <div className="text-[11px] opacity-60 px-4 py-2 border-t" style={{ borderColor: "var(--wp-border)" }}>
              DIN A4 · für den Druck bei Kräuterwanderungen. QR-Code führt zur Live-Pflanzenseite auf wildkräuter.lu.
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
